import { useState } from 'react';
import clsx from 'clsx';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ScriptCheckView } from '@1qa/shared';
import { CheckCircle2, CircleAlert, ShieldCheck, XCircle } from '@/components/icons';
import { api, errorMessage } from '@/lib/api';
import { formatDateTime, timeAgo } from '@/lib/format';
import { useRunCollection } from '@/lib/runCollection';
import { RunCollectionPicker } from '@/components/testdata/RunCollectionPicker';
import { Badge, Button, Card, CardBody, CardHeader, Select, Spinner, useToast, type Tone } from '@/components/ui';
import { VerifyLines } from './PerfReport';

const active = (c: ScriptCheckView) => c.status === 'QUEUED' || c.status === 'RUNNING';
const STATUS: Record<ScriptCheckView['status'], { label: string; tone: Tone }> = {
  QUEUED: { label: 'Waiting for an agent', tone: 'pending' },
  RUNNING: { label: 'Running', tone: 'info' },
  PASSED: { label: 'Works', tone: 'success' },
  FAILED: { label: 'Does not work', tone: 'danger' },
  ERROR: { label: 'Could not check', tone: 'warning' },
  CANCELLED: { label: 'Stopped', tone: 'muted' },
};

/** The outcome of one check, in words, above its requests. */
function Outcome({ check }: { check: ScriptCheckView }) {
  const s = check.summary;
  if (active(check)) {
    return (
      <p role="status" className="flex items-center gap-2 text-fg-secondary">
        <Spinner className="size-4 text-primary" />
        {check.status === 'QUEUED' ? 'Waiting for a load agent to take the check…' : 'One user is going through the journey…'}
      </p>
    );
  }
  if (check.status === 'PASSED') {
    return (
      <p role="status" className="flex items-start gap-2 rounded-field bg-success-subtle p-3 text-fg ring-1 ring-success/40 ring-inset">
        <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" />
        <span>
          The script works: every request answered as recorded{s ? ` (${s.verify.length} requests in ${Math.max(1, Math.round(s.durationMs / 1000))} s)` : ''}. It is ready for a performance test.
        </span>
      </p>
    );
  }
  const failed = s?.verify.filter((l) => !l.ok).length ?? 0;
  return (
    <p role="alert" className={clsx('flex items-start gap-2 rounded-field p-3 text-fg ring-1 ring-inset', check.status === 'FAILED' ? 'bg-danger-subtle ring-danger-line' : 'bg-warning-subtle ring-warning/40')}>
      {check.status === 'FAILED' ? <XCircle className="mt-0.5 size-4 shrink-0 text-danger-text" /> : <CircleAlert className="mt-0.5 size-4 shrink-0 text-warning" />}
      <span>
        {check.status === 'FAILED' && s
          ? `${failed} of ${s.verify.length} request${s.verify.length === 1 ? '' : 's'} did not answer as recorded. Open them under “Requests, step by step”, fix them, save and check again.`
          : check.status === 'CANCELLED'
            ? 'The check was stopped.'
            : (check.error ?? s?.reason ?? 'The check could not run.')}
        {check.status === 'FAILED' && s?.reason ? <span className="mt-1 block text-xs text-fg-secondary">{s.reason}</span> : null}
      </span>
    </p>
  );
}

/**
 * "Check the script": one user goes through the saved script once on a free load agent, request by request, before the
 * script goes into a performance test. Shows the latest check (refreshing while it runs) and earlier ones.
 */
export function ScriptChecks({ projectId, scriptId, canRun, unsaved }: { projectId: string; scriptId: string; canRun: boolean; unsaved: boolean }) {
  const toast = useToast();
  const qc = useQueryClient();
  const runData = useRunCollection(projectId);
  const [selected, setSelected] = useState<string | null>(null);
  // Test data: the project's, or the first row of a data file.
  const dataSets = useQuery({ queryKey: ['perf-data-sets', projectId], queryFn: () => api.perfDataSets(projectId) });
  const [dataSetId, setDataSetId] = useState('');
  const checks = useQuery({
    queryKey: ['perf-script-checks', scriptId],
    queryFn: () => api.perfScriptChecks(scriptId),
    refetchInterval: (q) => (q.state.data?.some(active) ? 2000 : false),
  });
  const start = useMutation({
    mutationFn: () => api.checkPerfScript(scriptId, runData.collectionId, dataSetId || null),
    onSuccess: (c) => {
      setSelected(c.id);
      qc.invalidateQueries({ queryKey: ['perf-script-checks', scriptId] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const list = checks.data ?? [];
  const shown = list.find((c) => c.id === selected) ?? list[0];
  const running = list.some(active);

  return (
    <Card>
      <CardHeader
        title="Check the script"
        description="One user goes through the journey once on a load agent, request by request, with the project’s test data. Do it before using the script in a performance test."
        actions={
          canRun && (
            <div className="flex flex-wrap items-center gap-3">
              <RunCollectionPicker state={runData} />
              {!!dataSets.data?.length && (
                <Select
                  aria-label="Test data for the check"
                  value={dataSetId}
                  onChange={(e) => setDataSetId(e.target.value)}
                  options={[{ value: '', label: 'Project test data' }, ...dataSets.data.map((d) => ({ value: d.id, label: `${d.name}: row 1` }))]}
                />
              )}
              <Button
                onClick={() => start.mutate()}
                loading={start.isPending}
                disabled={unsaved || running}
                title={unsaved ? 'Save your changes first: the check runs the saved script' : running ? 'A check is running' : undefined}
                icon={<ShieldCheck className="size-4" />}
              >
                Check the script (1 user)
              </Button>
            </div>
          )
        }
      />
      <CardBody className="space-y-4 text-sm">
        {unsaved && canRun && <p className="text-fg-secondary">Save your changes first: the check runs the saved script.</p>}
        {!shown ? (
          <p className="text-fg-muted">Not checked yet.</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 text-xs text-fg-muted">
              <Badge tone={STATUS[shown.status].tone}>{STATUS[shown.status].label}</Badge>
              <span>
                {formatDateTime(shown.queuedAt)} by {shown.triggeredBy?.name ?? 'someone'}
                {shown.collectionName ? ` · ${shown.collectionName} test data` : ''}
              </span>
            </div>
            <Outcome check={shown} />
            {shown.summary && shown.summary.verify.length > 0 && (
              <>
                <div className="rounded-card ring-1 ring-border-subtle ring-inset">
                  <VerifyLines lines={shown.summary.verify} />
                </div>
                {!shown.summary.verify.some((l) => l.request) && (
                  <p className="text-xs text-fg-muted">
                    The load agent that ran this check is older than 1.1, so it sent no request and response details. Download the agent again from Helper services ›
                    Load agents and restart it.
                  </p>
                )}
              </>
            )}
            {list.length > 1 && (
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="text-fg-muted">Earlier checks:</span>
                {list.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    aria-pressed={c.id === shown.id}
                    onClick={() => setSelected(c.id)}
                    className={clsx('rounded-full px-2.5 py-0.5 ring-1 ring-inset', c.id === shown.id ? 'ring-primary text-fg' : 'text-fg-secondary ring-border hover:ring-primary')}
                  >
                    {STATUS[c.status].label} · {timeAgo(c.queuedAt)}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
}
